import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import '../../App.css';
import {createCourse} from "../../api";
import RichTextEditor from '../../components/RichTextEditor';

const CourseCreate = () => {
    const navigate = useNavigate();
    const [courseData, setCourseData] = useState({
        name: '',
        description: '',
        level: 'beginner',
        image: '',
        chapters: []
    });
    const [expandedChapters, setExpandedChapters] = useState({});
    const [expandedLessons, setExpandedLessons] = useState({});
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        const token = localStorage.getItem('token');
        if (!token) {
            navigate('/');
        }
    }, [navigate]);

    const token = localStorage.getItem('token');
    if (!token) {
        return null;
    }

    const addChapter = () => {
        const newChapter = {
            chapter_index: courseData.chapters.length + 1,
            chapter_name: '',
            lessons: []
        };
        setCourseData({
            ...courseData,
            chapters: [...courseData.chapters, newChapter]
        });
    };

    const removeChapter = (chapterIndex) => {
        const updatedChapters = courseData.chapters
            .filter((_, idx) => idx !== chapterIndex)
            .map((chapter, idx) => ({
                ...chapter,
                chapter_index: idx + 1
            }));
        setCourseData({
            ...courseData,
            chapters: updatedChapters
        });
    };

    const updateChapter = (chapterIndex, field, value) => {
        const updatedChapters = [...courseData.chapters];
        updatedChapters[chapterIndex] = {
            ...updatedChapters[chapterIndex],
            [field]: value
        };
        setCourseData({
            ...courseData,
            chapters: updatedChapters
        });
    };

    const addLesson = (chapterIndex) => {
        const chapter = courseData.chapters[chapterIndex];
        const newLesson = {
            lessons_index: chapter.lessons.length + 1,
            start_title: '',
            start_description: '',
            start_image: '',
            fragments: []
        };
        const updatedChapters = [...courseData.chapters];
        updatedChapters[chapterIndex].lessons = [...chapter.lessons, newLesson];
        setCourseData({
            ...courseData,
            chapters: updatedChapters
        });
    };

    const removeLesson = (chapterIndex, lessonIndex) => {
        const updatedChapters = [...courseData.chapters];
        updatedChapters[chapterIndex].lessons = updatedChapters[chapterIndex].lessons
            .filter((_, idx) => idx !== lessonIndex)
            .map((lesson, idx) => ({
                ...lesson,
                lessons_index: idx + 1
            }));
        setCourseData({
            ...courseData,
            chapters: updatedChapters
        });
    };

    const updateLesson = (chapterIndex, lessonIndex, field, value) => {
        const updatedChapters = [...courseData.chapters];
        updatedChapters[chapterIndex].lessons[lessonIndex] = {
            ...updatedChapters[chapterIndex].lessons[lessonIndex],
            [field]: value
        };
        setCourseData({
            ...courseData,
            chapters: updatedChapters
        });
    };

    const addFragment = (chapterIndex, lessonIndex) => {
        const lesson = courseData.chapters[chapterIndex].lessons[lessonIndex];
        const newFragment = {
            fragment_index: lesson.fragments.length + 1,
            type: 'text',
            title: '',
            content: '',
            image_list: []
        };
        const updatedChapters = [...courseData.chapters];
        updatedChapters[chapterIndex].lessons[lessonIndex].fragments = [
            ...lesson.fragments,
            newFragment
        ];
        setCourseData({
            ...courseData,
            chapters: updatedChapters
        });
    };

    const removeFragment = (chapterIndex, lessonIndex, fragmentIndex) => {
        const updatedChapters = [...courseData.chapters];
        updatedChapters[chapterIndex].lessons[lessonIndex].fragments =
            updatedChapters[chapterIndex].lessons[lessonIndex].fragments
                .filter((_, idx) => idx !== fragmentIndex)
                .map((fragment, idx) => ({
                    ...fragment,
                    fragment_index: idx + 1
                }));
        setCourseData({
            ...courseData,
            chapters: updatedChapters
        });
    };

    const updateFragment = (chapterIndex, lessonIndex, fragmentIndex, field, value) => {
        const updatedChapters = [...courseData.chapters];
        updatedChapters[chapterIndex].lessons[lessonIndex].fragments[fragmentIndex] = {
            ...updatedChapters[chapterIndex].lessons[lessonIndex].fragments[fragmentIndex],
            [field]: value
        };
        setCourseData({
            ...courseData,
            chapters: updatedChapters
        });
    };

    const addImageToFragment = (chapterIndex, lessonIndex, fragmentIndex) => {
        const updatedChapters = [...courseData.chapters];
        const fragment = updatedChapters[chapterIndex].lessons[lessonIndex].fragments[fragmentIndex];
        fragment.image_list = [...fragment.image_list, ''];
        setCourseData({
            ...courseData,
            chapters: updatedChapters
        });
    };

    const updateFragmentImage = (chapterIndex, lessonIndex, fragmentIndex, imageIndex, value) => {
        const updatedChapters = [...courseData.chapters];
        const fragment = updatedChapters[chapterIndex].lessons[lessonIndex].fragments[fragmentIndex];
        fragment.image_list[imageIndex] = value;
        setCourseData({
            ...courseData,
            chapters: updatedChapters
        });
    };

    const removeFragmentImage = (chapterIndex, lessonIndex, fragmentIndex, imageIndex) => {
        const updatedChapters = [...courseData.chapters];
        const fragment = updatedChapters[chapterIndex].lessons[lessonIndex].fragments[fragmentIndex];
        fragment.image_list = fragment.image_list.filter((_, idx) => idx !== imageIndex);
        setCourseData({
            ...courseData,
            chapters: updatedChapters
        });
    };

    const toggleChapter = (chapterIndex) => {
        setExpandedChapters({
            ...expandedChapters,
            [chapterIndex]: !expandedChapters[chapterIndex]
        });
    };

    const toggleLesson = (chapterIndex, lessonIndex) => {
        const key = `${chapterIndex}-${lessonIndex}`;
        setExpandedLessons({
            ...expandedLessons,
            [key]: !expandedLessons[key]
        });
    };

    const isContentEmpty = (content) => {
        if (!content) return true;
        const text = content.replace(/<[^>]*>/g, '').trim();
        return text === '' || text === '\n';
    };

    const validateCourseData = () => {
        for (const chapter of courseData.chapters) {
            for (const lesson of chapter.lessons) {
                for (const fragment of lesson.fragments) {
                    if (isContentEmpty(fragment.content)) {
                        return 'All fragments must have content. Please fill in the content field for all fragments.';
                    }
                }
            }
        }
        return null;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        
        const validationError = validateCourseData();
        if (validationError) {
            setError(validationError);
            return;
        }

        setLoading(true);

        try {
            console.log('Course data:', courseData);
            await createCourse(courseData);
            navigate('/course-list');
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to create course.');
        } finally {
            setLoading(false);
        }
    };

    const inputStyle = {
        width: '100%',
        padding: '12px 16px',
        borderRadius: '14px',
        border: '1px solid #cfd8dc',
        background: '#e5eff8',
        fontSize: '16px',
        color: '#0c1415',
        marginBottom: '12px'
    };

    const labelStyle = {
        display: 'block',
        marginBottom: '8px',
        color: '#3c5a5d',
        fontWeight: '600',
        fontSize: '14px'
    };

    const sectionStyle = {
        background: '#F6F6F6',
        borderRadius: '16px',
        padding: '20px',
        marginBottom: '20px',
        border: '1px solid #cfd8dc'
    };

    const nestedSectionStyle = {
        background: '#e5eff8',
        borderRadius: '12px',
        padding: '16px',
        marginBottom: '16px',
        border: '1px solid #cfd8dc'
    };

    return (
        <div style={{
            minHeight: '100vh',
            background: '#ccdff5',
            padding: '40px 20px'
        }}>
            <div style={{
                maxWidth: '1400px',
                margin: '0 auto',
                background: '#F6F6F6',
                borderRadius: '20px',
                padding: '40px',
                boxShadow: '0 4px 24px rgba(44, 62, 80, 0.08)'
            }}>
                <h1 style={{
                    color: '#3c5a5d',
                    marginBottom: '30px',
                    fontSize: '2.5rem'
                }}>
                    Create Course
                </h1>

                {error && (
                    <div style={{
                        color: '#dc2626',
                        marginBottom: '20px',
                        padding: '12px',
                        background: '#fee2e2',
                        borderRadius: '8px',
                        fontSize: '14px'
                    }}>
                        {error}
                    </div>
                )}

                <form onSubmit={handleSubmit}>
                    {/* Course Level Fields */}
                    <div style={sectionStyle}>
                        <h2 style={{ color: '#3c5a5d', marginBottom: '20px' }}>Course Information</h2>

                        <label style={labelStyle}>Course Name *</label>
                        <input
                            type="text"
                            value={courseData.name}
                            onChange={(e) => setCourseData({ ...courseData, name: e.target.value })}
                            required
                            style={inputStyle}
                            placeholder="Enter course name"
                        />

                        <label style={labelStyle}>Description *</label>
                        <textarea
                            value={courseData.description}
                            onChange={(e) => setCourseData({ ...courseData, description: e.target.value })}
                            required
                            style={{ ...inputStyle, minHeight: '100px', resize: 'vertical' }}
                            placeholder="Enter course description"
                        />

                        <label style={labelStyle}>Level *</label>
                        <select
                            value={courseData.level}
                            onChange={(e) => setCourseData({ ...courseData, level: e.target.value })}
                            required
                            style={inputStyle}
                        >
                            <option value="beginner">Beginner</option>
                            <option value="intermediate">Intermediate</option>
                            <option value="advanced">Advanced</option>
                        </select>

                        <label style={labelStyle}>Course Image URL</label>
                        <input
                            type="text"
                            value={courseData.image}
                            onChange={(e) => setCourseData({ ...courseData, image: e.target.value })}
                            style={inputStyle}
                            placeholder="Enter image URL"
                        />
                    </div>

                    {/* Chapters */}
                    <div style={sectionStyle}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                            <h2 style={{ color: '#3c5a5d', margin: 0 }}>Chapters</h2>
                            <button
                                type="button"
                                onClick={addChapter}
                                className="btn btn-secondary"
                                style={{ padding: '10px 20px' }}
                            >
                                + Add Chapter
                            </button>
                        </div>

                        {courseData.chapters.map((chapter, chapterIndex) => (
                            <div key={chapterIndex} style={nestedSectionStyle}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                                    <h3 style={{ color: '#3c5a5d', margin: 0 }}>
                                        Chapter {chapter.chapter_index}
                                    </h3>
                                    <div>
                                        <button
                                            type="button"
                                            onClick={() => toggleChapter(chapterIndex)}
                                            className="btn btn-secondary"
                                            style={{ padding: '8px 16px', marginRight: '8px', fontSize: '14px' }}
                                        >
                                            {expandedChapters[chapterIndex] ? '−' : '+'}
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => removeChapter(chapterIndex)}
                                            className="btn btn-secondary"
                                            style={{ padding: '8px 16px', fontSize: '14px', background: '#fee2e2', color: '#dc2626' }}
                                        >
                                            Remove
                                        </button>
                                    </div>
                                </div>

                                {expandedChapters[chapterIndex] && (
                                    <>
                                        <label style={labelStyle}>Chapter Name *</label>
                                        <input
                                            type="text"
                                            value={chapter.chapter_name}
                                            onChange={(e) => updateChapter(chapterIndex, 'chapter_name', e.target.value)}
                                            required
                                            style={inputStyle}
                                            placeholder="Enter chapter name"
                                        />

                                        {/* Lessons */}
                                        <div style={{ marginTop: '20px' }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                                                <h4 style={{ color: '#3c5a5d', margin: 0 }}>Lessons</h4>
                                                <button
                                                    type="button"
                                                    onClick={() => addLesson(chapterIndex)}
                                                    className="btn btn-secondary"
                                                    style={{ padding: '8px 16px', fontSize: '14px' }}
                                                >
                                                    + Add Lesson
                                                </button>
                                            </div>

                                            {chapter.lessons.map((lesson, lessonIndex) => {
                                                const lessonKey = `${chapterIndex}-${lessonIndex}`;
                                                return (
                                                    <div key={lessonIndex} style={{
                                                        background: '#F6F6F6',
                                                        borderRadius: '10px',
                                                        padding: '16px',
                                                        marginBottom: '12px',
                                                        border: '1px solid #cfd8dc'
                                                    }}>
                                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                                                            <h5 style={{ color: '#3c5a5d', margin: 0 }}>
                                                                Lesson {lesson.lessons_index}
                                                            </h5>
                                                            <div>
                                                                <button
                                                                    type="button"
                                                                    onClick={() => toggleLesson(chapterIndex, lessonIndex)}
                                                                    className="btn btn-secondary"
                                                                    style={{ padding: '6px 12px', marginRight: '8px', fontSize: '12px' }}
                                                                >
                                                                    {expandedLessons[lessonKey] ? '−' : '+'}
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    onClick={() => removeLesson(chapterIndex, lessonIndex)}
                                                                    className="btn btn-secondary"
                                                                    style={{ padding: '6px 12px', fontSize: '12px', background: '#fee2e2', color: '#dc2626' }}
                                                                >
                                                                    Remove
                                                                </button>
                                                            </div>
                                                        </div>

                                                        {expandedLessons[lessonKey] && (
                                                            <>
                                                                <label style={labelStyle}>Start Title *</label>
                                                                <input
                                                                    type="text"
                                                                    value={lesson.start_title}
                                                                    onChange={(e) => updateLesson(chapterIndex, lessonIndex, 'start_title', e.target.value)}
                                                                    required
                                                                    style={inputStyle}
                                                                    placeholder="Enter lesson start title"
                                                                />

                                                                <label style={labelStyle}>Start Description *</label>
                                                                <textarea
                                                                    value={lesson.start_description}
                                                                    onChange={(e) => updateLesson(chapterIndex, lessonIndex, 'start_description', e.target.value)}
                                                                    required
                                                                    style={{ ...inputStyle, minHeight: '80px', resize: 'vertical' }}
                                                                    placeholder="Enter lesson start description"
                                                                />

                                                                <label style={labelStyle}>Start Image URL</label>
                                                                <input
                                                                    type="text"
                                                                    value={lesson.start_image}
                                                                    onChange={(e) => updateLesson(chapterIndex, lessonIndex, 'start_image', e.target.value)}
                                                                    style={inputStyle}
                                                                    placeholder="Enter start image URL"
                                                                />

                                                                {/* Fragments */}
                                                                <div style={{ marginTop: '16px' }}>
                                                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                                                                        <h6 style={{ color: '#3c5a5d', margin: 0 }}>Fragments</h6>
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => addFragment(chapterIndex, lessonIndex)}
                                                                            className="btn btn-secondary"
                                                                            style={{ padding: '6px 12px', fontSize: '12px' }}
                                                                        >
                                                                            + Add Fragment
                                                                        </button>
                                                                    </div>

                                                                    {lesson.fragments.map((fragment, fragmentIndex) => (
                                                                        <div key={fragmentIndex} style={{
                                                                            background: '#e5eff8',
                                                                            borderRadius: '8px',
                                                                            padding: '12px',
                                                                            marginBottom: '12px',
                                                                            border: '1px solid #cfd8dc'
                                                                        }}>
                                                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                                                                                <span style={{ color: '#3c5a5d', fontWeight: '600' }}>
                                                                                    Fragment {fragment.fragment_index}
                                                                                </span>
                                                                                <button
                                                                                    type="button"
                                                                                    onClick={() => removeFragment(chapterIndex, lessonIndex, fragmentIndex)}
                                                                                    className="btn btn-secondary"
                                                                                    style={{ padding: '4px 10px', fontSize: '11px', background: '#fee2e2', color: '#dc2626' }}
                                                                                >
                                                                                    Remove
                                                                                </button>
                                                                            </div>

                                                                            <label style={labelStyle}>Type *</label>
                                                                            <select
                                                                                value={fragment.type}
                                                                                onChange={(e) => updateFragment(chapterIndex, lessonIndex, fragmentIndex, 'type', e.target.value)}
                                                                                required
                                                                                style={inputStyle}
                                                                            >
                                                                                <option value="text">Text</option>
                                                                                <option value="practice">Practice</option>
                                                                            </select>

                                                                            <label style={labelStyle}>Title</label>
                                                                            <input
                                                                                type="text"
                                                                                value={fragment.title}
                                                                                onChange={(e) => updateFragment(chapterIndex, lessonIndex, fragmentIndex, 'title', e.target.value)}
                                                                                style={inputStyle}
                                                                                placeholder="Enter fragment title (optional)"
                                                                            />

                                                                            <label style={labelStyle}>Content *</label>
                                                                            <RichTextEditor
                                                                                value={fragment.content || ''}
                                                                                onChange={(value) => updateFragment(chapterIndex, lessonIndex, fragmentIndex, 'content', value)}
                                                                                placeholder="Enter fragment content"
                                                                                required={true}
                                                                            />

                                                                            <div style={{ marginTop: '12px' }}>
                                                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                                                                                    <label style={labelStyle}>Image List</label>
                                                                                    <button
                                                                                        type="button"
                                                                                        onClick={() => addImageToFragment(chapterIndex, lessonIndex, fragmentIndex)}
                                                                                        className="btn btn-secondary"
                                                                                        style={{ padding: '4px 10px', fontSize: '11px' }}
                                                                                    >
                                                                                        + Add Image
                                                                                    </button>
                                                                                </div>
                                                                                {fragment.image_list.map((image, imageIndex) => (
                                                                                    <div key={imageIndex} style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                                                                                        <input
                                                                                            type="text"
                                                                                            value={image}
                                                                                            onChange={(e) => updateFragmentImage(chapterIndex, lessonIndex, fragmentIndex, imageIndex, e.target.value)}
                                                                                            style={{ ...inputStyle, marginBottom: 0, flex: 1 }}
                                                                                            placeholder="Enter image URL"
                                                                                        />
                                                                                        <button
                                                                                            type="button"
                                                                                            onClick={() => removeFragmentImage(chapterIndex, lessonIndex, fragmentIndex, imageIndex)}
                                                                                            className="btn btn-secondary"
                                                                                            style={{ padding: '12px 16px', background: '#fee2e2', color: '#dc2626' }}
                                                                                        >
                                                                                            ×
                                                                                        </button>
                                                                                    </div>
                                                                                ))}
                                                                            </div>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            </>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </>
                                )}
                            </div>
                        ))}
                    </div>

                    <button
                        type="submit"
                        disabled={loading}
                        className="btn btn-primary"
                        style={{
                            width: '100%',
                            padding: '15px',
                            fontSize: '18px',
                            marginTop: '20px'
                        }}
                    >
                        {loading ? 'Creating Course...' : 'Create Course'}
                    </button>
                </form>
            </div>
        </div>
    );
}

export default CourseCreate;
